import { create, destroy, EVENT, } from 'zoid/dist/zoid.frameworks.frame';
import { getContainerCss } from './components/loader/css';
import { loaderComponentHtml } from './components/loader/html';
import { getCSS } from './components/modal/css';
import { getModalWrapper } from './components/modal/html';
export const CONNECT_EVENTS = [
    'SUBMITTED_CONSENT',
    'SELECTED_INSTITUTION',
    'SUBMITTED_LOGIN',
    'SUBMITTED_MFA',
];
// this is the URL where the widget component is being hosted
const urlByEnv = {
    local: 'http://localhost:3000',
    development: 'https://dev.connect.pluggy.ai',
    production: 'https://connect.pluggy.ai',
};
let zoidComponentInstance;
const noOp = () => {
    return;
};
/**
 * Instantiates a singleton ZoidComponent reference, that will take care of
 * wrapping and rendering the actual ZoidComponentInstance.
 *
 * This reference should only be instantiated once per page/context.
 */
const init = () => {
    if (!zoidComponentInstance) {
        zoidComponentInstance = create({
            // The html tag used to render the component
            tag: 'pluggy-connect-widget',
            // The url of the page that will show in the iframe or popup, when someone includes the component on their site
            url: ({ props }) => {
                const { env, url: urlByProp } = props;
                if (urlByProp) {
                    // url defined by prop, use it
                    return urlByProp;
                }
                // use url from 'env', default to 'production'
                const envOrDefault = env || 'production';
                return urlByEnv[envOrDefault];
            },
            dimensions: {
                width: '320px',
                height: '568px',
            },
            props: {
                url: {
                    type: 'string',
                    required: false,
                },
                env: {
                    type: 'string',
                    required: false,
                },
                connectToken: {
                    type: 'string',
                },
                includeSandbox: {
                    type: 'boolean',
                    default: () => false,
                },
                updateItem: {
                    type: 'string',
                    required: false,
                },
                connectorTypes: {
                    type: 'array',
                    required: false,
                },
                connectorIds: {
                    type: 'array',
                    required: false,
                },
                countries: {
                    type: 'array',
                    required: false,
                },
                language: {
                    type: 'string',
                    required: false,
                },
                onSuccess: {
                    type: 'function',
                    required: false,
                    default: () => noOp,
                },
                onError: {
                    type: 'function',
                    required: false,
                    default: () => noOp,
                },
                onOpen: {
                    type: 'function',
                    required: false,
                    default: () => noOp,
                },
                onClose: {
                    type: 'function',
                    required: false,
                    default: () => noOp,
                },
                onEvent: {
                    type: 'function',
                    required: false,
                    default: () => noOp,
                },
            },
            attributes: {
                iframe: {
                    scrolling: 'no',
                    title: 'Pluggy',
                },
                popup: {
                    scrolling: 'no',
                    title: 'Pluggy',
                },
            },
            prerenderTemplate({ doc, props }) {
                const htmlNew = doc.createElement('html');
                htmlNew.innerHTML = loaderComponentHtml({
                    nonce: props.cspNonce,
                });
                return htmlNew;
            },
            containerTemplate({ doc, dimensions: { height, width }, close, uid, frame, prerenderFrame, event, props, }) {
                if (!prerenderFrame || !frame) {
                    throw new Error('Unexpected state: prerenderFrame or frame not defined');
                }
                const container = doc.createElement('div');
                container.id = uid;
                container.innerHTML = getModalWrapper({ uid });
                const frameContainer = container.querySelector(`.${uid}_container`);
                if (!frameContainer) {
                    throw new Error('Unexpected state: not found frame container');
                }
                const visibleClassName = `${uid}-visible`;
                const invisibleClassName = `${uid}-invisible`;
                frameContainer.appendChild(frame);
                frameContainer.appendChild(prerenderFrame);
                // Add styles
                const style = doc.createElement('style');
                style.innerHTML = [
                    getCSS({ uid, themeColor: 'fafafa', height, width }),
                    getContainerCss({ uid }),
                ].join('\n');
                container.appendChild(style);
                prerenderFrame.classList.add(`${uid}-prerender-frame`);
                prerenderFrame.classList.add(visibleClassName);
                frame.classList.add(invisibleClassName);
                event.on(EVENT.RENDERED, () => {
                    // hide prerender frame, display content frame
                    prerenderFrame.classList.remove(visibleClassName);
                    prerenderFrame.classList.add(invisibleClassName);
                    frame.classList.remove(invisibleClassName);
                    frame.classList.add(visibleClassName);
                });
                // Remove scrolling from document body when visible
                const modalVisibleClassName = `${uid}_has_modal_visible`;
                event.on(EVENT.DISPLAY, () => {
                    // modal displayed
                    document.body.classList.add(modalVisibleClassName);
                    const { onOpen } = props;
                    if (typeof onOpen === 'function') {
                        onOpen();
                    }
                });
                event.on(EVENT.CLOSE, () => {
                    // modal closed
                    document.body.classList.remove(modalVisibleClassName);
                    const { onClose } = props;
                    if (typeof onClose === 'function') {
                        onClose();
                    }
                });
                // Register modal close() on 'Escape' key button press
                document.addEventListener('keydown', function escapeKeyCloseHandler(event) {
                    if (event.key !== 'Escape') {
                        return;
                    }
                    // escape key -> close (and remove 'keydown' listener)
                    close();
                    document.removeEventListener('keydown', escapeKeyCloseHandler);
                });
                return container;
            },
        });
    }
    return zoidComponentInstance;
};
export class PluggyConnect {
    constructor(props) {
        this.zoidComponent = init();
        this.componentProps = props;
    }
    /**
     * Render the component using the specified component props,
     * as a modal with an iframe, appended to the page body (or the DOM uppermost element).
     *
     * @returns promise that resolves when rendered successfully, or throws if failed.
     */
    init() {
        const container = (document.getElementsByTagName('body') ||
            document.getElementsByTagName('html') ||
            document.getElementsByTagName('div'))[0];
        this.componentInstance = this.zoidComponent(this.componentProps);
        return this.componentInstance.render(container).catch((error) => {
            console.error(`Failed to render <PluggyConnect /> component`, error);
            throw error;
        });
    }
    /**
     * Manually cleanup the component.
     * This is not recommended, you should create the component once
     * and re-render it as needed each new time to save resources.
     */
    destroy() {
        zoidComponentInstance = undefined;
        return destroy();
    }
    /**
     * Helper method to be executed from the child component.
     * Used to just create the zoid instance, needed for the internal handshake
     * with the parent instance that will be rendering it.
     */
    static register() {
        init();
    }
}
export default PluggyConnect;
//# sourceMappingURL=data:application/json;base64,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